'use client'

import { useImperativeHandle } from 'react'

import useOnceEffect from '../../hooks/once-effect'
import useUpdateEffect from '../../hooks/update-effect'

import { useTableContext, useTableStore } from '../context/table-context'
import { TTableRefObject } from '../types/table-props'

type TProps<T> = {
    ref?: TTableRefObject<T> | null
}

const TableEffects = <T,>({ ref }: TProps<T>) => {
    const { store, onRowSelected, onHiddenColumnsChanged, id } = useTableContext<T>()
    // subscribe only to what the callbacks need, the handle reads the store lazily
    const selectedRows = useTableStore<T, 'selectedRows'>('selectedRows')
    const hiddenColumns = useTableStore<T, 'hiddenColumns'>('hiddenColumns')

    useUpdateEffect(() => {
        onRowSelected?.([...selectedRows.values()])
    }, [selectedRows])

    useUpdateEffect(() => {
        onHiddenColumnsChanged?.(hiddenColumns)
    }, [hiddenColumns])

    useOnceEffect(() => {
        const { data, refreshData } = store.get()
        if (data.length == 0)
            refreshData()
    })

    useImperativeHandle(
        ref,
        () => ({
            refreshData: (clearSelectedRows?: boolean) => store.get().refreshData(clearSelectedRows),
            // all selected rows (also from other pages), the same as `onRowSelected` gets
            getSelectedRows: () => [...store.get().selectedRows.values()],
            getData: () => store.get().data,
            setRow: (row: T | T[]) => store.setRow(row, id),
            removeRow: (row: T) => store.removeRow(row, id),
            getHiddenColumns: () => store.get().hiddenColumns,
        }),
        [store, id]
    )

    return null
}

export default TableEffects
